import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { HeaderComponent } from '../../../shared/salesforce/header/header.component';
import { PasswordInputComponent } from '../../../shared/password-input/password-input.component';
import { ErrorMessageService } from '../../shared/services/error-message.service';
import { RedirectHelperService } from '../../shared/services/redirect-helper.service';
import { UserAuthService } from '../../shared/services/user-auth.service';

@Component({
  selector: 'app-user-login',
  imports: [CommonModule, FormsModule, RouterModule, HeaderComponent, TranslateModule, PasswordInputComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class UserLoginComponent implements OnInit, OnDestroy {
  username = '';
  password = '';
  errorMessage: string | null = null;
  usernameTouched = false;
  passwordTouched = false;
  loading = false;
  
  private errorCode: string | null = null;
  private errorDefaultMessage: string | null = null;
  private langChangeSubscription?: Subscription;

  constructor(
    private authService: UserAuthService,
    private router: Router,
    private translate: TranslateService,
    private redirectHelper: RedirectHelperService,
    private errorMessageService: ErrorMessageService
  ) {}

  ngOnInit(): void {
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.updateErrorMessage();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  private updateErrorMessage(): void {
    if (this.errorCode && this.errorDefaultMessage) {
      this.errorMessage = this.errorMessageService.getErrorMessage(
        this.errorCode,
        this.errorDefaultMessage
      );
    }
  }

  private setError(errorCode: string, defaultMessage: string): void {
    this.errorCode = errorCode;
    this.errorDefaultMessage = defaultMessage;
    this.updateErrorMessage();
  }

  private clearError(): void {
    this.errorCode = null;
    this.errorDefaultMessage = null;
    this.errorMessage = null;
  }

  isFormValid(): boolean {
    return !!this.username && !!this.password;
  }

  validateUsername() {
    this.usernameTouched = true;
  }

  validatePassword() {
    this.passwordTouched = true;
  }

  login() {
    this.usernameTouched = true;
    this.passwordTouched = true;
    this.clearError();

    if (!this.isFormValid()) {
      return;
    }

    this.loading = true;
    this.authService
      .validateCredentials(this.username, this.password)
      .subscribe({
        next: (response) => {
          this.loading = false;
          if (response.errorCode === '0' && response.data?.authCode) {
            const authCode = response.data.authCode;
            const selectedRole = response.data?.selectedRole || 'N_GS';

            this.redirectHelper.redirectByRole(authCode, selectedRole);
          } else if (response.errorCode === '1010' && response.data && response.data.sessionId && response.data.trxId) {
            sessionStorage.setItem('user_sessionId', response.data.sessionId);
            sessionStorage.setItem('user_trxId', response.data.trxId);
            sessionStorage.setItem('user_username', this.username);
            sessionStorage.setItem('user_initialMethod', 'email');

            if (response.data?.selectedRole) {
              sessionStorage.setItem('user_selectedRole', response.data.selectedRole);
            }

            this.router.navigate(['/customidp/user/verify']);
          } else {
            this.setError(
              response.errorCode || '9999',
              this.translate.instant('SALESFORCE.LOGIN.LOGIN_FAILED')
            );
          }
        },
        error: (error) => {
          this.loading = false;
          this.setError('9999', this.translate.instant('SALESFORCE.LOGIN.LOGIN_ERROR'));
          console.error('User login error:', error);
        },
      });
  }

}